<template>
  <v-dialog :value="value" width="1200" persistent>
    <v-card style="position: relative" :loading="modal.pendingTransformRequest" >
      <AdCardTitle dialog>
          {{ $t('transform-from-transaction') }}
      </AdCardTitle>

      <v-card-text class="pa-4">
        <v-row>
          <v-col cols="5">
            <TreeviewPanel
              class="treeview"
              ref="treeviewPanel"
              hideTabs
              :trees="treeviewData"
              :filters="filters"
              :identifier="'uuid'"
              :editable="false"
              @search="handleSearch"
              :active-item.sync="modal.selectedTreeviewItem"
            />
          </v-col>

          <v-col>
            <v-card v-if="modal.selectedTreeviewItem">
              <div class="detail-card-header">
                <AdCardTitle class="flex-grow-1">{{ detailCardTitle }}</AdCardTitle>
                <v-card-subtitle
                  v-if="sourceDatasetInfo.name"
                  key="subtitle"
                  class="subtitle-2 pt-2"
                >
                  {{ $t('source-dataset') }}: {{ $filters.getMatchingTranslation({ value: sourceDatasetInfo.name }) }}
                </v-card-subtitle>
              </div>

              <v-card-text class="pt-4">
                <EditableArtefactDetailHeader
                  :artefact="'Transaction'"
                  :item="modal.selectedTreeviewItem"
                  :locales="projectLocales"
                  readonly
                  :hasDetails="false"
                />
              </v-card-text>
            </v-card>
            <AdInfoOwl v-else>{{ $t('no-transaction-selected') }}</AdInfoOwl>
          </v-col>
        </v-row>
      </v-card-text>

      <v-divider></v-divider>

      <v-card-actions>
        <v-spacer></v-spacer>
        <AdButton type="cancel" :disabled="modal.pendingTransformRequest" @click="closeModal"/>
        <AdButton type="add" :disabled="!modal.selectedTreeviewItem || modal.pendingTransformRequest" @click="transformTransaction" />
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { cloneDeep } from 'lodash'
import { mapGetters } from 'vuex'
import TreeviewPanel from '@/components/Utilities/Treeview/TreeviewPanel'
import EditableArtefactDetailHeader from '../Utilities/Editable/EditableStructure/EditableArtefactDetailHeader.vue'

const initialModal = {
  pendingTransformRequest: false,
  selectedTreeviewItem: null,
  search: ''
}
export default {
  name: 'QuestionnaireTransformDialog',
  components: {
    EditableArtefactDetailHeader,
    TreeviewPanel
  },
  props: {
    // Open/close state of the modal
    value: {
      type: Boolean,
      required: false,
      default: false
    }

  },
  data () {
    return {
      modal: cloneDeep(initialModal),
      filters: []
    }
  },
  computed: {
    ...mapGetters('server', ['getItemStatuses']),

    detailCardTitle () {
      if (!this.modal.selectedTreeviewItem) {
        return ''
      }
      return this.modal.selectedTreeviewItem.displayName || this.$filters.getMatchingTranslation({ value: this.modal.selectedTreeviewItem.name })
    },
    fhirObjectFormats () {
      return this.$store.getters['server/getDecorTypes']?.data?.FhirObjectFormat?.[0]?.enumeration || []
    },
    fhirURL () {
      return this.$store.getters['server/serverSettings']?.['url-fhir-services']?.[0]
    },

    sourceDatasetInfo () {
      return {
        type: 'DS',
        id: this.modal.selectedTreeviewItem?.representingTemplate?.[0]?.sourceDataset,
        name: this.modal.selectedTreeviewItem?.representingTemplate?.[0]?.sourceDatasetName,
        statusCode: this.modal.selectedTreeviewItem?.representingTemplate?.[0]?.sourceDatasetStatusCode,
        flexibility: this.modal.selectedTreeviewItem?.representingTemplate?.[0]?.sourceDatasetFlexibility
          ? this.modal.selectedTreeviewItem.representingTemplate?.[0]?.sourceDatasetFlexibility
          : 'dynamic'
      }
    },

    treeviewData () {
      // Items tree with scenarios disabled
      return [{
        artefact: 'TR',
        loading: this.$store.state.scenario.loading.scenarioItems,
        loaded: this.$store.state.scenario.loaded.scenarioItems,
        items: this.prepareTreeviewItems(this.$store.getters['scenario/scenarioItems']),
        menu: false,
        insertable: false,
        editable: false,
        expandable: true,
        key: 'id',
        text: (item, returnObject) => this.$filters.getMatchingTranslation({
          value: (item?.name || []).map((name) => ({
            ...name,
            '#text': item?.versionLabel
              ? `${name?.['#text']} (${item?.versionLabel})`
              : name?.['#text']
          })),
          selector: '#text',
          returnObject
        }),
        children: 'transaction',
        onSelect: (activeItem) => {
          if (activeItem) {
            this.selectedObjects = [activeItem]
          } else {
            this.selectedObjects = []
          }
        }

      }]
    },

    watchDataForFilters () {
      return [this.getItemStatuses, this.$i18n.locale]
    }
  },
  methods: {
    closeModal () {
      this.modal = cloneDeep(initialModal)
      this.$emit('input', false)
    },
    async fetchTransactions () {
      try {
        await this.$store.dispatch('scenario/loadScenarioItems')
      } catch (error) {
        const errorMessage = error?.response?.data?.description || error?.response?.statusText || 'Error'
        this.$oops(errorMessage)
      }
    },
    handleSearch (search) {
      this.modal.search = search || ''
    },
    prepareTreeviewItems (items) {
      if (!Array.isArray(items)) {
        return []
      }
      return items.map(obj => {
        return {
          ...obj,
          disabled: !['initial', 'back', 'stationary'].includes(obj.type),
          transaction: Array.isArray(obj.transaction) ? this.prepareTreeviewItems(obj.transaction) : null
        }
      })
    },
    async transformTransaction () {
      if (!this.modal.selectedTreeviewItem) {
        this.$oops('no-transaction-selected')
        return
      }

      const params = {
        project: this.projectInFocus?.prefix
      }
      const data = {
        transaction: [
          {
            id: this.modal.selectedTreeviewItem.id,
            effectiveDate: this.modal.selectedTreeviewItem.effectiveDate
          }
        ]
      }
      try {
        this.modal.pendingTransformRequest = true
        const res = await this.$store.dispatch('scenario/postTransactionQuestionnaire', { params, data })
        this.$store.dispatch('project/scheduledTasks/setScheduledTasks', res)
        this.closeModal()
        this.$notify(
          this.$t('questionnaire-transform-in-progress-hint'),
          'info',
          { long: true })
      } catch (error) {
        const errorMessage = error?.response?.data?.description || error?.response?.statusText || 'Error'
        this.$oops(errorMessage)
      }
      this.modal.pendingTransformRequest = false
    }
  },
  watch: {
    value: {
      immediate: true,
      handler (modalIsOpen) {
        if (modalIsOpen) {
          if (!this.$store.state.scenario.loaded.scenarioItems) {
            this.fetchTransactions()
          }
        }
      }
    },

    watchDataForFilters: {
      immediate: true,
      deep: true,
      handler () {
        const defaultActive = ['new', 'draft', 'pending', 'final']
        this.filters = this.getItemStatuses?.map(status => {
          const currentlyActive = this.filters?.find(filter => filter.key === status.value)?.active
          return {
            name: this.$filters.getMatchingTranslation({ value: status.label, locale: this.$i18n.locale }),
            key: status.value,
            active: currentlyActive !== undefined
              ? currentlyActive
              : defaultActive.includes(status.value)
          }
        }) || ['']
        const referenceFilterActive = this.filters.find(filter => filter.key === undefined)?.active
        this.filters.push({
          name: this.$t('none'),
          key: undefined,
          active: referenceFilterActive !== undefined
            ? referenceFilterActive
            : defaultActive.includes('undefined')
        })
      }
    }
  }
}
</script>

<style lang="scss" scoped>
::v-deep .required .v-label:after {
  content: "*";
  font-weight: bold;
  color: var(--v-error-base);
}

.detail-card-header {
  height: 72px;
  background: var(--v-topbottomstrip-base);
}

.treeview ::v-deep .v-window__container {
  max-height: 520px;
  overflow-y: auto;
}

</style>
